import { useState } from 'react'
import { router } from '@inertiajs/react'
import Layout, { Empty } from '@/components/Layout'
import { Badge, Button, Chip, Field, Fold, List, Row, Section, fieldInputClass } from '@/components/ui'
import { Sheet, SheetPad } from '@/components/Sheet'
import { toast } from '@/components/Toast'
import { useCanWrite } from '@/lib/access'
import StarterTemplates, { StarterUndoBar, type StarterTemplate } from '@/components/StarterTemplates'
import type { GoalSummary } from '@/types'
import { t } from '@/lib/i18n'

// 总分门槛的常用值。0 = 不设门槛，所以它也是一个 chip。
const TARGETS = [0, 50, 100, 200, 500]

export default function Index({ goals, starter_templates }: { goals: GoalSummary[]; starter_templates: StarterTemplate[] }) {
  const canWrite = useCanWrite()
  const [adding, setAdding] = useState(false)
  const [title, setTitle] = useState('')
  const [deadline, setDeadline] = useState('')
  const [target, setTarget] = useState('')

  const live = goals.filter((g) => !g.archived)
  const archived = goals.filter((g) => g.archived)

  const add = () => {
    const name = title.trim()
    router.post('/goals', { title: name, deadline, target_points: target }, {
      onSuccess: () => {
        setAdding(false)
        toast(t('goals.index.toast_added', { title: name }))
        setTitle(''); setDeadline(''); setTarget('')
      },
    })
  }

  // 一行目标。归档区与在做区共用同一个渲染，避免两处漂移。
  const row = (g: GoalSummary) => (
    <Row
      key={g.id}
      onTap={() => router.visit(`/goals/${g.id}`)}
      ariaLabel={g.title}
      title={
        <span className="flex items-center gap-1.5">
          <span className="truncate">{g.title}</span>
          {g.status === 'done' && <Badge tone="green">{t('goals.index.status_done')}</Badge>}
          {/* 归档徽章与状态徽章**并存**：只显示「已归档」会把「这个目标当初达成了没有」
              藏起来，而那正是用 archived_at 而非枚举值想保住的信息。 */}
          {g.archived && (
            <span className="shrink-0 px-2.5 py-1 rounded-full text-xs font-bold bg-sunk text-muted">
              {t('goals.index.status_archived')}
            </span>
          )}
        </span>
      }
      sub={
        g.target_points > 0 ? (
          <span className="block">
            <span>{t('goals.index.progress', { earned: g.earned, target: g.target_points, pct: g.progress_pct })}</span>
            <span className="block h-[5px] mt-[7px] rounded-full bg-sunk overflow-hidden">
              <span
                className="block h-full rounded-full bg-gradient-to-r from-brand to-brand2"
                style={{ width: `${g.progress_pct}%` }}
              />
            </span>
          </span>
        ) : (
          t('goals.index.earned', { count: g.earned })
        )
      }
      pts={t('goals.index.steps_count', { count: g.tasks_count })}
    />
  )

  return (
    <Layout bleed>
      <StarterUndoBar />

      {live.length === 0 && archived.length === 0 ? (
        <div className="mx-3 mt-3">
          <StarterTemplates templates={starter_templates} />
        </div>
      ) : live.length === 0 ? (
        <Empty
          emoji="🎯"
          title={t('goals.index.empty_title')}
          action={canWrite ? <Button variant="ghost" onClick={() => setAdding(true)}>＋ {t('goals.index.add')}</Button> : undefined}
        >
          {t('goals.index.empty')}
        </Empty>
      ) : (
        // 计数只数未归档的：归档的收在下面折叠区里，算进标题会让数字和看到的行数不符
        <Section title={`🎯 ${t('goals.index.title')}`} tail={t('goals.index.count_hint', { count: live.length })}>
          <List>{live.map(row)}</List>
        </Section>
      )}

      {archived.length > 0 && (
        <Fold summary={t('goals.index.archived_section', { count: archived.length })}>
          <List>{archived.map(row)}</List>
        </Fold>
      )}

      {/* 新建目标的主操作也固定在拇指区（同详情页的加步骤），取代原来那一行内联表单。 */}
      {canWrite && live.length > 0 && (
        <>
          <div className="h-[74px]" />
          <div className="fixed left-3 right-3 z-[35] above-tabbar max-w-[536px] mx-auto">
            <Button onClick={() => setAdding(true)}>＋ {t('goals.index.add')}</Button>
          </div>
        </>
      )}

      <Sheet open={adding} onClose={() => setAdding(false)} label={t('goals.index.add')} title={t('goals.index.add')}>
        <SheetPad>
          <Field label={t('goals.show.field_title')}>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('goals.index.new_placeholder')}
              enterKeyHint="done"
              autoComplete="off"
              className={fieldInputClass}
            />
          </Field>
          <Field label={t('goals.show.field_target')}>
            <div className="flex gap-2 flex-wrap">
              {TARGETS.map((n) => (
                <Chip key={n} pressed={target === String(n)} onClick={() => setTarget(String(n))}>
                  {n === 0 ? t('goals.index.target_none') : n}
                </Chip>
              ))}
            </div>
            <input
              type="number" inputMode="numeric" min={0} value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder={t('goals.show.no_target')}
              className={fieldInputClass + ' mt-2'}
            />
          </Field>
          <Field label={t('goals.show.field_deadline')}>
            <input type="date" value={deadline} onChange={(e) => setDeadline(e.target.value)} className={fieldInputClass} />
          </Field>
          <Button disabled={!title.trim()} onClick={add}>{t('goals.index.add')}</Button>
        </SheetPad>
      </Sheet>
    </Layout>
  )
}
